import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Stack,
  Grid,
  useTheme,
  useMediaQuery,
  Card,
  CardMedia,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SEO from '../components/SEO';
import FederalEmployeeDialog from '../components/FederalEmployeeDialog';
import { generateWebPageSchema } from '../utils/schemaGenerators';

// Import hero image
import HeroImage from '../assets/images/garza-trump.jpg';
import NavigateToButton from '../components/NavigateToButton';
import TimeTakenTimeBackImage from '../../src/assets/images/time-taken-time-back.png';
import ThreeStrikesImage from '../../src/assets/images/three-strikes.png';
import LitigantAccessImage from '../../src/assets/images/litigant-access-economy-act.png';
import AppealableOrdersImage from '../../src/assets/images/appealable-temporary-orders.png';

const FamilyFirstFederalReform: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [federalDialogOpen, setFederalDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);

  const handleFederalDialogOpen = () => {
    setFederalDialogOpen(true);
  };

  const handleFederalDialogClose = () => {
    setFederalDialogOpen(false);
  };

  const handleFederalDialogSubmit = (formData: any) => {
    // Show professional success dialog
    setFederalDialogOpen(false);
    setSuccessDialogOpen(true);
  };

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
  };

  return (
    <Container component="main" maxWidth={false}>
      <SEO
        title="RobertGarza.us Family Court Reform - Federal Reform"
        description="Join Robert Garza in advancing family-first reform at the federal level. Partner with us to promote parental rights, accountability, and comprehensive reform."
        path="/family-first-federal-reform"
        schema={generateWebPageSchema({
          name: 'RobertGarza.us Family Court Reform - Federal Reform',
          description:
            'Join Robert Garza in advancing family-first reform at the federal level. Partner with us to promote parental rights, accountability, and comprehensive reform.',
          url: '/family-first-federal-reform',
        })}
      />

      {/* Hero Banner */}
      <Box
        sx={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'flex-start',
          textAlign: 'left',
          marginX: { xs: -2, md: -4 },
          padding: { xs: 4, md: 20 },
          backgroundImage: (theme) => ({
            xs: `linear-gradient(to top, ${theme.palette.background.default} 0%, transparent 170%), url(${HeroImage})`,
            md: `linear-gradient(90deg, ${theme.palette.background.default} 0%, ${theme.palette.background.default} 30%, transparent 70%), url(${HeroImage})`,
          }),
          backgroundPosition: {
            xs: 'left top, -400px top', // gradient stays, image shifts on mobile
            md: 'center right, center right', // both layers aligned on desktop
          },
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <Box
          sx={{
            maxWidth: { xs: '100%', md: '800px' },
          }}
        >
          <Typography variant="h1" component="h1" gutterBottom sx={{ fontWeight: '800', mb: 1 }}>
            Join the Movement for Family-First Reform at the Federal Level
          </Typography>
          <Typography variant="h3" component="h3" sx={{ fontWeight: '200', lineHeight: '1.5', mb: 4 }}>
            Partner with Robert Garza to Advance Parental Rights, Transparency, and Accountability in America's Family
            Courts
          </Typography>
          <Stack direction={isMobile ? 'column' : 'row'} spacing={2}>
            <Button
              variant="contained"
              size="large"
              onClick={handleFederalDialogOpen}
              sx={{ backgroundColor: 'primary.main', px: 4, py: 2 }}
            >
              🏛️ Join Federal Coalition
            </Button>
            <Button variant="contained" size="large" href="/family-court-reform-bills" sx={{ px: 4, py: 2 }}>
              Explore Bills
            </Button>
          </Stack>
        </Box>
      </Box>

      {/* Main Content */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 1, md: 4 } }}>
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            Building a Federal Coalition for Family Court Reform
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 4 }}>
            Federal employees play a vital role in shaping policy that protects families, promotes fairness, and upholds
            constitutional rights. Robert Garza—a nationally recognized advocate for family court reform—is building a
            coalition of committed professionals to help advance a Family First legislative agenda in Washington and
            beyond.
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6 }}>
            With decades of firsthand experience navigating a broken system, Garza has led the charge on common-sense,
            bipartisan reforms like:
          </Typography>

          {/* Reform Bills Grid */}
          <Grid container spacing={4} sx={{ mb: 8 }}>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 3 }}>
                <CardMedia component="img" height="500" image={TimeTakenTimeBackImage} alt="Time Taken, Time Back" />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Time Taken Time Back
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Restores parenting time lost due to unfounded allegations or precautionary removals.
                  </Typography>
                  <NavigateToButton onClick={() => {}} billSlug="time-taken-time-back" sx={{ width: '100%' }}>
                    Learn More
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 3 }}>
                <CardMedia component="img" height="500" image={ThreeStrikesImage} alt="Three Strikes Law" />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Three Strikes
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Holding repeat custody violators accountable
                  </Typography>
                  <NavigateToButton onClick={() => {}} billSlug="three-strikes" sx={{ width: '100%' }}>
                    Learn More
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 3 }}>
                <CardMedia
                  component="img"
                  height="500"
                  image={LitigantAccessImage}
                  alt="Litigant Access and Economy Act"
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    The Litigant Access and Economy Act
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Reducing court costs and barriers through remote access
                  </Typography>
                  <NavigateToButton onClick={() => {}} billSlug="litigant-access-economy-act" sx={{ width: '100%' }}>
                    Learn More
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ boxShadow: 3 }}>
                <CardMedia
                  component="img"
                  height="500"
                  image={AppealableOrdersImage}
                  alt="Making Temporary Orders Appealable"
                />
                <CardContent>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Appealable Temporary Orders
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    Ending unchecked judicial overreach
                  </Typography>
                  <NavigateToButton onClick={() => {}} billSlug="appealable-temporary-orders" sx={{ width: '100%' }}>
                    Learn More
                  </NavigateToButton>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 6 }}>
            Now, he's expanding this mission federally—and seeking dedicated federal employees who believe in restoring
            balance, fairness, and transparency to civil justice.
          </Typography>
        </Container>
      </Box>

      {/* Why It Matters Section */}
      <Box
        sx={{
          py: { xs: 8, md: 20 },
          px: { xs: 4 },
          marginX: -4,
          bgcolor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[100]),
        }}
      >
        <Container>
          <Typography variant="h2" sx={{ fontWeight: 'bold', textAlign: 'center', mb: 6 }}>
            Why It Matters
          </Typography>
          <Grid container spacing={6} justifyContent="center">
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                📊 Impact Scale
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                Millions of parents and children are affected by biased or outdated family court systems
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                🏛️ Federal Need
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                Federal advocacy is essential to establish national standards for due process and equal parenting
              </Typography>
            </Grid>
            <Grid item xs={12} md={4} textAlign="center">
              <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 3, color: 'primary.main' }}>
                💪 Your Power
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: '1.6' }}>
                Your insight, experience, and position can directly influence meaningful, long-overdue change
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Call to Action Section */}
      <Box sx={{ py: { xs: 8, md: 20 }, px: { xs: 4 } }}>
        <Container>
          <Box textAlign="center">
            <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 2 }}>
              Ready to Get Involved?
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.2rem', lineHeight: '1.8', mb: 4 }}>
              If you work in policy, legal affairs, human rights, child welfare, or public administration—your
              experience matters. This is your chance to stand on the right side of reform and help reshape the future
              of family justice.
            </Typography>

            <Typography variant="body1" sx={{ fontSize: '1.1rem', mb: 4 }}>
              Click below to connect with Robert Garza and explore meaningful ways to collaborate, advise, or support
              the Family First legislative agenda at the federal level.
            </Typography>
            <Stack direction={isMobile ? 'column' : 'row'} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                size="large"
                onClick={handleFederalDialogOpen}
                sx={{
                  fontSize: '1.2rem',
                  px: 4,
                  py: 2,
                  backgroundColor: 'secondary.main',
                }}
              >
                🏛️ Join Federal Coalition
              </Button>
            </Stack>
          </Box>
        </Container>
      </Box>

      {/* Federal Employee Dialog */}
      <FederalEmployeeDialog
        open={federalDialogOpen}
        onClose={handleFederalDialogClose}
        onSubmit={handleFederalDialogSubmit}
      />

      {/* Success Dialog */}
      <Dialog open={successDialogOpen} onClose={handleSuccessDialogClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CheckCircleIcon
              sx={{
                fontSize: 48,
                color: 'success.main',
                mb: 1,
              }}
            />
            <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'success.main' }}>
              Application Submitted Successfully!
            </Typography>
          </Box>
          <IconButton
            aria-label="close"
            onClick={handleSuccessDialogClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ textAlign: 'center', px: 4, pb: 2 }}>
          <Typography variant="body1" sx={{ fontSize: '1.1rem', lineHeight: 1.6, mb: 2 }}>
            Your application has been received and submitted directly to our team.
          </Typography>

          <Typography variant="body1" sx={{ fontSize: '1rem', lineHeight: 1.6, color: 'text.secondary' }}>
            Someone from our team will reach out to you shortly to discuss next steps and how you can best contribute to
            advancing family court reform at the federal level.
          </Typography>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
          <Button onClick={handleSuccessDialogClose} variant="contained" size="large" sx={{ px: 4 }}>
            Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default FamilyFirstFederalReform;
